/**
 * Baked AR controller — the 360 configurator's "View in your space".
 *
 * ── Why this is the ONLY correct AR for a shipped configurator ────────────────
 * iOS Quick Look ignores scene lights and renders the model dark/flat from a live
 * three.USDZExporter export. The validated process (brain:
 * methodology/2026-06-20-ar-baked-usdz-mandatory) is to ship a PRE-BAKED USDZ per
 * config — the 360's lighting frozen into an emissive texture — so AR looks exactly
 * like the 360. This controller resolves that baked file by the SAME slug the spin
 * frames use (resolveSlug) and hands it straight to Quick Look / Scene Viewer:
 *   - iOS      → /perf-assets/bungalow-studio/ar/<slug>.usdz   (Quick Look)
 *   - Android  → /perf-assets/bungalow-studio/ar/<slug>.glb    (Scene Viewer)
 *   - Desktop  → QR (unchanged, ArLauncher)
 * A manifest (ar/index.json `{slugs:[…]}`) says which configs are baked; configs
 * without a bake have their AR button HIDDEN (never a 404 / broken tap).
 *
 * NO live USDZ export here — that's the whole point. The live ArController stays for
 * the 3D configurator; the 360 uses this.
 */

import { launchAR, prepareAR, maybeAutoLaunchAR } from "./ArLauncher";
import { showToast } from "./Toast";
import { resolveSlug } from "@/state/slug";
import type { ArController } from "./ArController";
import type { Signal } from "@/state/Store";
import type { ConfigState, ProductConfig } from "@/types";

/** Dossier AR par defaut (Bungalow). Un produit peut pointer ailleurs via `spin.arBase`. */
const AR_BASE_DEFAULT = "/perf-assets/bungalow-studio/ar";

function absUrl(u: string): string {
  try { return new URL(u, location.href).toString(); }
  catch { return u; }
}

export function createBakedArController(
  product: ProductConfig,
  state: Signal<ConfigState>,
): ArController {
  /** Slugs that actually have a baked USDZ+GLB (from ar/index.json). Empty until
   *  loaded → AR button stays hidden, never offers a broken tap. */
  let baked = new Set<string>();
  const AR_BASE = product.spin?.arBase ?? AR_BASE_DEFAULT;
  void (async () => {
    try {
      const idx = await (await fetch(`${AR_BASE}/index.json`, { cache: "no-store" })).json();
      baked = new Set<string>(idx.slugs ?? []);
    } catch {
      // No manifest → no baked AR for this product. Button stays hidden.
      console.warn("[baked-ar] no ar/index.json — AR disabled");
    }
  })();

  const currentSlug = (): string | null => resolveSlug(product, state.get());
  const sourcesFor = (slug: string) => ({
    usdz: absUrl(`${AR_BASE}/${slug}.usdz`),
    glb: absUrl(`${AR_BASE}/${slug}.glb`),
  });

  // Android: prefetch the baked GLB for the current config on confirmed WiFi only.
  const slug0 = currentSlug();
  if (slug0) prepareAR({ glb: sourcesFor(slug0).glb });

  return {
    /** True when the given slug has a baked AR file — drives the AR button visibility. */
    hasAr(slug: string): boolean {
      return baked.has(slug);
    },
    launch(): void {
      const slug = currentSlug();
      if (!slug || !baked.has(slug)) {
        showToast("AR isn't ready for this option yet");
        return;
      }
      launchAR(sourcesFor(slug))();
    },
    maybeAutoLaunch(): void {
      const slug = currentSlug();
      if (slug && baked.has(slug)) maybeAutoLaunchAR(sourcesFor(slug));
    },
    dispose(): void {
      /* nothing to release — no blob URLs are created in the baked path */
    },
  };
}
